import { Modal, Text, Button, Group, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useState, useCallback } from 'react';
import type React from 'react';

export interface ConfirmModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: string;
    showIcon?: boolean;
    loading?: boolean;
}

export function ConfirmModal({
    opened,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmColor = 'red',
    showIcon = true,
    loading = false,
}: ConfirmModalProps) {
    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={title}
            centered
            size="sm"
            closeOnClickOutside={!loading}
            closeOnEscape={!loading}
            styles={{
                root: { position: 'fixed', top: 0, left: 0 },
                overlay: { opacity: 0.7 },
                content: {
                    padding: '20px',
                    minHeight: '300px'
                }
            }}
        >
            <Stack gap="md">
                {showIcon && (
                    <Group justify="center">
                        <IconAlertCircle size={48} color="var(--mantine-color-orange-6)" />
                    </Group>
                )}

                <Text size="sm" c="dimmed" ta="center">
                    {message}
                </Text>

                <Group justify="space-between" gap="sm">
                    <Button
                        variant="default"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        color={confirmColor}
                        onClick={handleConfirm}
                        loading={loading}
                    >
                        {confirmLabel}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}

export interface UseConfirmModalOptions {
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: string;
    showIcon?: boolean;
}

export interface UseConfirmModalReturn {
    ConfirmModalComponent: React.FC;
    openConfirmModal: (onConfirm: () => void, options?: UseConfirmModalOptions) => void;
}

export function useConfirmModal(): UseConfirmModalReturn {
    const [opened, setOpened] = useState(false);
    const [onConfirmCallback, setOnConfirmCallback] = useState<(() => void) | null>(null);
    const [modalOptions, setModalOptions] = useState<UseConfirmModalOptions>({});

    const openConfirmModal = useCallback((onConfirm: () => void, options?: UseConfirmModalOptions) => {
        setOnConfirmCallback(() => onConfirm);
        setModalOptions(options || {});
        setOpened(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpened(false);
        setOnConfirmCallback(null);
        setModalOptions({});
    }, []);

    const handleConfirm = useCallback(() => {
        if (onConfirmCallback) {
            onConfirmCallback();
        }
        handleClose();
    }, [onConfirmCallback, handleClose]);

    const ConfirmModalComponent: React.FC = useCallback(() => (
        <ConfirmModal
            opened={opened}
            onClose={handleClose}
            onConfirm={handleConfirm}
            title={modalOptions.title}
            message={modalOptions.message}
            confirmLabel={modalOptions.confirmLabel}
            cancelLabel={modalOptions.cancelLabel}
            confirmColor={modalOptions.confirmColor}
            showIcon={modalOptions.showIcon}
        />
    ), [opened, handleClose, handleConfirm, modalOptions]);

    return {
        ConfirmModalComponent,
        openConfirmModal,
    };
}

export default ConfirmModal;
